import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { url } from '../../../../../env';
import OperationalReviewModal from '../shared/OperationalReviewModal';
import {
    AppTextField, AppNumberField, AppButton, AppIconButton,
} from '../../../../../Components/FormKit';
import { AppTableCard } from '../../../../../Components/TableKit';
import CapacidadPagoDictamen from './CapacidadPagoDictamen';

// ─── Constantes de color ──────────
const PURPLE = '#7B1FA2';
const PURPLE_LIGHT = '#F3E8FF';

// ─── Helpers de formato ──────────
const formatCurrency = (val) => {
    if (val === null || val === undefined) return <span className="text-500 font-italic">N/A</span>;
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);
};

const formatRatio = (val) => {
    if (val === null || val === undefined || isNaN(val) || !isFinite(val)) return <span className="text-500 font-italic">N/A</span>;
    return val.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + 'x';
};

// ─── Componentes reusables ──────────
const isCalcValidDoc = (doc) => {
    return doc.review_status !== 'rejected' && !['pending', 'processing', 'failed'].includes(doc.status);
};

// Agrupa documentos por month_key o file_key
const groupDocuments = (docs) => {
    const grouped = {};

    docs.forEach(doc => {
        const key = doc.month_key || doc.file_key;
        if (!grouped[key]) {
            grouped[key] = {
                key,
                month_label: doc.month_label,
                period_end_date: doc.period_end_date,
                docs: [],
                is_valid_for_calc: false, // True si TODO es válido, o combinamos? Usaremos docs en la fila
                sums: {
                    saldo_inicial: 0,
                    depositos: 0,
                    retiros: 0,
                    saldo_final: 0,
                    promedio_mes: 0,
                    numero_movimientos: 0
                },
                source_types: new Set()
            };
        }

        grouped[key].docs.push(doc);

        if (isCalcValidDoc(doc)) {
            grouped[key].is_valid_for_calc = true;
            grouped[key].sums.saldo_inicial += (doc.saldo_inicial || 0);
            grouped[key].sums.depositos += (doc.depositos || 0);
            grouped[key].sums.retiros += (doc.retiros || 0);
            grouped[key].sums.saldo_final += (doc.saldo_final || 0);
            grouped[key].sums.promedio_mes += (doc.promedio_mes || 0);
            grouped[key].sums.numero_movimientos += (doc.numero_movimientos || 0);
            grouped[key].source_types.add(doc.promedio_source_type);
            // Track accuracy scores for averaging
            if (doc.accuracy != null) {
                grouped[key].sums._accuracySum = (grouped[key].sums._accuracySum || 0) + doc.accuracy;
                grouped[key].sums._accuracyCount = (grouped[key].sums._accuracyCount || 0) + 1;
            }
        }
    });

    const result = Object.values(grouped).sort((a, b) => {
        if (a.period_end_date && b.period_end_date) return b.period_end_date.localeCompare(a.period_end_date);
        if (a.period_end_date) return -1;
        if (b.period_end_date) return 1;
        return 0;
    });

    return result;
};

const getChipButtonStyle = (isActive, activeColor) => ({
    background: isActive ? `${activeColor}15` : 'transparent',
    border: isActive ? `1px solid ${activeColor}` : '1px solid #e5e7eb',
    padding: '0.35rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: isActive ? '600' : '500',
    color: isActive ? activeColor : '#64748b',
    borderRadius: '20px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    whiteSpace: 'nowrap',
});

// Tabla principal
const CapPagoTable = ({ actor, documents, rentaPei, onViewDoc }) => {
    const actorDocs = documents.filter(d => d.actor === actor);
    const rows = groupDocuments(actorDocs);
    const validRows = rows.filter(r => r.is_valid_for_calc);
    const n = validRows.length;

    // Calcular promedios
    const avg = {
        saldo_inicial: n ? validRows.reduce((acc, r) => acc + r.sums.saldo_inicial, 0) / n : 0,
        depositos: n ? validRows.reduce((acc, r) => acc + r.sums.depositos, 0) / n : 0,
        retiros: n ? validRows.reduce((acc, r) => acc + r.sums.retiros, 0) / n : 0,
        saldo_final: n ? validRows.reduce((acc, r) => acc + r.sums.saldo_final, 0) / n : 0,
        promedio_mes: n ? validRows.reduce((acc, r) => acc + r.sums.promedio_mes, 0) / n : 0,
        numero_movimientos: n ? validRows.reduce((acc, r) => acc + r.sums.numero_movimientos, 0) / n : 0,
    };

    const hasSourceFallback = n && validRows.some(r => r.source_types.has('fallback'));
    const sourceTypesMerged = new Set();
    if (n) {
        validRows.forEach(r => r.source_types.forEach(s => sourceTypesMerged.add(s)));
    }
    const finalSourceLabel = sourceTypesMerged.has('extracted') && sourceTypesMerged.has('fallback') ? 'Mixto' : (sourceTypesMerged.has('fallback') ? 'Calculado (Fallback)' : 'Extraído');

    if (!actorDocs.length) {
        return <div className="text-sm text-500 font-italic py-3">No hay documentos de este actor.</div>;
    }

    return (
        <div className="flex flex-column gap-3">
            <AppTableCard animated={false}>
                <table className="tk-table tk-table--compact tk-table--striped">
                    <thead>
                        <tr>
                            <th>Mes</th>
                            <th className="tk-th-right">Saldo Inicial</th>
                            <th className="tk-th-right">Depósitos</th>
                            <th className="tk-th-right">Retiros</th>
                            <th className="tk-th-right">Saldo Final</th>
                            <th className="tk-th-right">Promedio</th>
                            <th className="tk-th-center">Mov.</th>
                            <th className="tk-th-center">Puntuación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const isProcessing = row.docs.some(d => d.status === 'processing' || d.status === 'pending');
                            const isRejected = row.docs.length > 0 && row.docs.every(d => d.review_status === 'rejected');
                            const showFallbackLabel = row.source_types.has('fallback');

                            return (
                                <tr key={row.key}>
                                    <td className="p-2 font-medium text-900 border-right-1 surface-border">
                                        <div className="flex align-items-center gap-2">
                                            {row.docs.length > 0 && (
                                                <button
                                                    className="p-link"
                                                    onClick={() => onViewDoc && onViewDoc(row.docs[0])}
                                                    style={{ color: PURPLE, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, outline: 'none' }}
                                                    title="Ver estado de cuenta"
                                                >
                                                    <i className="pi pi-eye" style={{ fontSize: '0.9rem' }} />
                                                </button>
                                            )}
                                            <div className="flex flex-column">
                                                <span>{row.month_label}</span>
                                                {showFallbackLabel && <span className="text-orange-500" style={{ fontSize: '0.65rem' }}>* Promedio calculado</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-2 tk-td-right">{row.is_valid_for_calc ? formatCurrency(row.sums.saldo_inicial) : <NA />}</td>
                                    <td className="p-2 tk-td-right">{row.is_valid_for_calc ? formatCurrency(row.sums.depositos) : <NA />}</td>
                                    <td className="p-2 tk-td-right text-red-500">{row.is_valid_for_calc ? formatCurrency(row.sums.retiros) : <NA />}</td>
                                    <td className="p-2 tk-td-right">{row.is_valid_for_calc ? formatCurrency(row.sums.saldo_final) : <NA />}</td>
                                    <td className="p-2 tk-td-right font-semibold" style={{ color: PURPLE }}>{row.is_valid_for_calc ? formatCurrency(row.sums.promedio_mes) : <NA />}</td>
                                    <td className="p-2 tk-td-center text-500">{row.is_valid_for_calc ? row.sums.numero_movimientos : <NA />}</td>
                                    <td className="p-2 tk-td-center">
                                        {isProcessing ? <Tag severity="warning" value="Procesando" rounded style={{ fontSize: '0.65rem' }} /> :
                                            isRejected ? <Tag severity="danger" value="Rechazado" rounded style={{ fontSize: '0.65rem' }} /> :
                                                !row.is_valid_for_calc ? <Tag severity="warning" value="Sin datos" rounded style={{ fontSize: '0.65rem' }} /> :
                                                    (() => {
                                                        const avgAcc = row.sums._accuracyCount
                                                            ? Math.round(row.sums._accuracySum / row.sums._accuracyCount)
                                                            : null;
                                                        if (avgAcc == null) return <Tag severity="info" value="—" rounded style={{ fontSize: '0.65rem' }} />;
                                                        const color = avgAcc >= 80 ? '#16a34a' : avgAcc >= 50 ? '#d97706' : '#dc2626';
                                                        const bg = avgAcc >= 80 ? '#dcfce7' : avgAcc >= 50 ? '#fef3c7' : '#fee2e2';
                                                        return (
                                                            <span style={{
                                                                fontSize: '0.7rem', fontWeight: 600, color,
                                                                background: bg, padding: '0.15rem 0.5rem',
                                                                borderRadius: '9999px', display: 'inline-block',
                                                            }}>
                                                                {avgAcc}%
                                                            </span>
                                                        );
                                                    })()}
                                    </td>
                                </tr>
                            );
                        })}
                        {/* Fila: Promedio */}
                        <tr className="surface-100" style={{ borderTop: '2px solid var(--surface-border)' }}>
                            <td className="p-2 font-bold text-900 border-right-1 surface-border">Promedio ({n} meses)</td>
                            <td className="p-2 tk-td-right font-medium">{n ? formatCurrency(avg.saldo_inicial) : <NA />}</td>
                            <td className="p-2 tk-td-right font-medium">{n ? formatCurrency(avg.depositos) : <NA />}</td>
                            <td className="p-2 tk-td-right font-medium text-red-500">{n ? formatCurrency(avg.retiros) : <NA />}</td>
                            <td className="p-2 tk-td-right font-medium">{n ? formatCurrency(avg.saldo_final) : <NA />}</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: PURPLE }}>{n ? formatCurrency(avg.promedio_mes) : <NA />}</td>
                            <td className="p-2 tk-td-center font-medium">{n ? Math.round(avg.numero_movimientos) : <NA />}</td>
                            <td className="p-2 tk-td-center"></td>
                        </tr>
                        {/* Fila: Renta PEI */}
                        <tr className="surface-50" style={{ borderBottom: '1px solid var(--surface-border)' }}>
                            <td className="p-2 font-bold text-900 border-right-1 surface-border">Renta PEI (c/ IVA)</td>
                            <td className="p-2 tk-td-right font-medium">{formatCurrency(rentaPei)}</td>
                            <td className="p-2 tk-td-right font-medium">{formatCurrency(rentaPei)}</td>
                            <td className="p-2 tk-td-right font-medium">{formatCurrency(rentaPei)}</td>
                            <td className="p-2 tk-td-right font-medium">{formatCurrency(rentaPei)}</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: PURPLE }}>{formatCurrency(rentaPei)}</td>
                            <td className="p-2 col-span-2"></td>
                            <td className="p-2 tk-td-center"></td>
                        </tr>
                        {/* Fila: Ratio */}
                        <tr style={{ background: '#F5F8FF' }}>
                            <td className="p-2 font-bold border-right-1 surface-border" style={{ color: '#1565C0' }}>Ratio de Cobertura</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: '#1565C0' }}>{n && rentaPei ? formatRatio(avg.saldo_inicial / rentaPei) : <NA />}</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: '#1565C0' }}>{n && rentaPei ? formatRatio(avg.depositos / rentaPei) : <NA />}</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: '#1565C0' }}>{n && rentaPei ? formatRatio(avg.retiros / rentaPei) : <NA />}</td>
                            <td className="p-2 tk-td-right font-bold" style={{ color: '#1565C0' }}>{n && rentaPei ? formatRatio(avg.saldo_final / rentaPei) : <NA />}</td>
                            <td className="p-2 tk-td-right font-bold text-lg" style={{ color: PURPLE }}>{n && rentaPei ? formatRatio(avg.promedio_mes / rentaPei) : <NA />}</td>
                            <td className="p-2 col-span-2"></td>
                            <td className="p-2 tk-td-center"></td>
                        </tr>
                    </tbody>
                </table>
            </AppTableCard>

            <div className="flex gap-3 mt-2">
                <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1', borderTop: `3px solid ${PURPLE}` }}>
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cobertura de Saldo</span>
                        <i className="pi pi-chart-line text-lg" style={{ color: PURPLE }} />
                    </div>
                    <div className="text-3xl font-bold" style={{ color: PURPLE }}>
                        {n && rentaPei ? formatRatio(avg.promedio_mes / rentaPei) : 'N/A'}
                    </div>
                    {/* Este ratio mide LIQUIDEZ (saldo promedio vs renta), no capacidad de
                        pago: no descuenta traspasos propios, no excluye prestamos y no
                        considera la deuda en Buro. El dictamen vive arriba, en el backend. */}
                    <div className="text-xs text-500 mt-1">Saldo promedio vs renta ({n} meses). Es referencia de liquidez, no el dictamen.</div>
                </div>
                <div className="bg-white border-1 surface-border border-round-lg p-3 shadow-1" style={{ flex: '1', borderTop: `3px solid #1565C0` }}>
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-sm font-bold text-900" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fuente del Promedio</span>
                        <i className="pi pi-database text-lg" style={{ color: '#1565C0' }} />
                    </div>
                    <div className="text-xl font-bold text-900 mb-1">
                        {n ? finalSourceLabel : 'N/A'}
                    </div>
                    <div className="text-xs text-500">
                        {n && hasSourceFallback ? 'Algunos meses se calcularon a partir de (Inicial + Final) / 2.' : 'Dato extraído directamente del banco.'}
                    </div>
                </div>
            </div>
        </div>
    );
};

const NA = () => <span className="text-500 font-italic">N/A</span>;

// ─── CapPagoEditForm ──────────────────────────────────────────────────────────
const CAPPAGO_EDIT_FIELDS = [
    { path: 'periodo', label: 'Periodo', type: 'text' },
    { path: 'saldo_inicial', label: 'Saldo Inicial', type: 'number', currency: true },
    { path: 'total_abonos', label: 'Total Abonos', type: 'number', currency: true },
    { path: 'total_cargos', label: 'Total Cargos', type: 'number', currency: true },
    { path: 'saldo_final', label: 'Saldo Final', type: 'number', currency: true },
    { path: 'saldo_promedio', label: 'Saldo Promedio', type: 'number', currency: true },
];

const CapPagoEditForm = ({ activeOverridePaths, editValues, saveReason, onFieldChange, onReasonChange, onClearPath, onSave, onCancel, saving }) => {
    return (
        <div className="fk-root">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }} className="mb-3">
                {CAPPAGO_EDIT_FIELDS.map(({ path, label, type, currency: isCurrency }) => {
                    const isConfirmed = activeOverridePaths.includes(path);
                    const customVars = isConfirmed
                        ? { '--fk-border': PURPLE, '--fk-bg': PURPLE_LIGHT }
                        : undefined;
                    const trailing = isConfirmed ? (
                        <>
                            <span className="text-xs font-semibold ml-1"
                                style={{ color: PURPLE, background: PURPLE_LIGHT, padding: '0 0.35rem', borderRadius: '4px' }}>
                                ✓ Confirmado
                            </span>
                            <AppIconButton icon="pi pi-undo" onClick={() => onClearPath(path)} title="Revertir al valor de IA" size="0.7rem" />
                        </>
                    ) : null;

                    if (type === 'number') {
                        return (
                            <div key={path} style={customVars}>
                                <AppNumberField
                                    label={label}
                                    value={editValues[path] != null ? Number(editValues[path]) : null}
                                    onValueChange={e => onFieldChange(path, e.value)}
                                    placeholder="N/A"
                                    trailing={trailing}
                                    mode={isCurrency ? 'currency' : undefined}
                                    currency={isCurrency ? 'MXN' : undefined}
                                    currencyDisplay={isCurrency ? 'narrowSymbol' : undefined}
                                    useGrouping={isCurrency}
                                    minFractionDigits={isCurrency ? 2 : undefined}
                                />
                            </div>
                        );
                    }
                    return (
                        <div key={path} style={customVars}>
                            <AppTextField
                                label={label}
                                value={editValues[path] ?? ''}
                                onChange={e => onFieldChange(path, e.target.value)}
                                placeholder="N/A"
                                trailing={trailing}
                            />
                        </div>
                    );
                })}
            </div>

            <AppTextField
                label="Motivo (opcional)"
                value={saveReason}
                onChange={e => onReasonChange(e.target.value)}
                placeholder="Ej: Corrección manual desde estado de cuenta"
            />

            <div className="flex justify-content-end gap-2" style={{ marginTop: '0.75rem' }}>
                <AppButton label="Cancelar" variant="secondary" size="sm" onClick={onCancel} disabled={saving} />
                <AppButton label="Guardar datos confirmados" variant="primary" size="sm"
                    onClick={onSave} loading={saving} />
            </div>
        </div>
    );
};

// ─── Main Component ──────────
const CapPagoAnalysisPanel = ({ persona, toast, dirty, onDirtyReset }) => {
    const [data, setData] = useState(null);

    const [loading, setLoading] = useState(false);
    const pollRef = useRef(null);
    const [selectedView, setSelectedView] = useState('pf');

    // ─── Override edit state ──────────────────────────────────────────────────
    const [modalDoc, setModalDoc] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [activeOverridePaths, setActiveOverridePaths] = useState([]);
    const [saveReason, setSaveReason] = useState('');
    const [clearPathsQueue, setClearPathsQueue] = useState([]);
    const [savingOverride, setSavingOverride] = useState(false);
    const [loadingOverrides, setLoadingOverrides] = useState(false);

    const openEditForm = useCallback(async (doc) => {
        // Open modal immediately with loading state
        setModalDoc(doc);
        setLoadingOverrides(true);
        setClearPathsQueue([]);
        setSaveReason('');

        const resolved = doc.resolved_extracted || {};
        const initialValues = {};
        CAPPAGO_EDIT_FIELDS.forEach(({ path }) => {
            const v = resolved[path];
            if (v !== null && v !== undefined) initialValues[path] = v;
        });
        setEditValues(initialValues);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setActiveOverridePaths((res.data.overrides || []).map(o => o.field_path));
        } catch {
            setActiveOverridePaths([]);
        } finally {
            setLoadingOverrides(false);
        }
    }, []);

    const handleClearPath = useCallback((path) => {
        setClearPathsQueue(q => q.includes(path) ? q : [...q, path]);
        setEditValues(v => { const n = { ...v }; delete n[path]; return n; });
        setActiveOverridePaths(p => p.filter(x => x !== path));
    }, []);

    const fetchData = useCallback(async (silent = false) => {
        if (!persona?.id) return;
        if (!silent) setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${url}/document-analysis/cap-pago/${persona.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setData({ documents: [] });
            } else {
                toast?.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron obtener los datos de Capacidad de Pago.', life: 3000 });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [persona?.id]);

    const saveOverrides = useCallback(async (doc) => {
        setSavingOverride(true);
        try {
            const token = localStorage.getItem('token');
            const values = {};
            CAPPAGO_EDIT_FIELDS.forEach(({ path, type }) => {
                const v = editValues[path];
                if (v !== undefined && v !== '' && v !== null) {
                    values[path] = type === 'number' ? Number(v) : v;
                }
            });
            await axios.post(
                `${url}/document-analysis/items/${doc.id}/operational-overrides`,
                {
                    category: doc.detected_type || 'EDOS CUENTA',
                    save_reason: saveReason || 'Corrección manual desde panel de capacidad de pago',
                    values,
                    clear_paths: clearPathsQueue,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast?.current?.show({ severity: 'success', summary: 'Guardado', detail: 'Datos confirmados guardados.', life: 3000 });
            setModalDoc(null);
            fetchData(true);
        } catch (err) {
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'No se pudieron guardar los datos.', life: 4000 });
        } finally {
            setSavingOverride(false);
        }
    }, [editValues, saveReason, clearPathsQueue, fetchData]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (dirty) {
            fetchData();
            onDirtyReset?.();
        }
    }, [dirty, fetchData, onDirtyReset]);

    const hasProcessing = data?.documents?.some(d => d.status === 'processing' || d.status === 'pending');
    const personaMode = data?.persona_mode;
    useEffect(() => {
        if (hasProcessing) {
            pollRef.current = setTimeout(() => fetchData(true), 10000);
        }
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [data, hasProcessing, fetchData]);

    useEffect(() => {
        if (personaMode === 'pm') {
            setSelectedView((current) => (['resumen', 'empresa', 'aval'].includes(current) ? current : 'resumen'));
            return;
        }

        setSelectedView('pf');
    }, [personaMode]);

    if (loading && !data) {
        return (
            <div className="flex flex-column gap-3 p-3">
                <style>{`@keyframes capSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
                <div style={{ height: '3rem', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'capSkel 1.4s infinite' }} />
                <div style={{ height: '20rem', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'capSkel 1.4s infinite' }} />
            </div>
        );
    }

    if (!data || !data.documents || data.documents.length === 0) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-4 gap-3" style={{ minHeight: '10rem' }}>
                <i className="pi pi-wallet text-3xl text-400" />
                <span className="text-sm text-500">No hay estados de cuenta analizados para calcular capacidad de pago.</span>
                <AppButton label="Actualizar" icon="pi pi-refresh" variant="secondary" size="sm" onClick={() => fetchData()} loading={loading} />
            </div>
        );
    }

    const { persona_mode, renta_pei, documents = [] } = data;

    const renderResumenPM = () => {
        const empresaDocs = documents.filter(d => d.actor === 'empresa');
        const avalDocs = documents.filter(d => d.actor === 'aval');

        const getStats = (docs) => {
            const rows = groupDocuments(docs);
            const valid = rows.filter(r => r.is_valid_for_calc);
            const n = valid.length;
            const avgPromedio = n ? valid.reduce((acc, r) => acc + r.sums.promedio_mes, 0) / n : null;
            const ratio = n && renta_pei ? avgPromedio / renta_pei : null;
            const hasFallback = n && valid.some(r => r.source_types.has('fallback'));
            const sourceType = n ? (hasFallback ? 'Mixto/Calculado' : 'Extraído') : 'N/A';
            return { n, avgPromedio, ratio, sourceType };
        };

        const empStats = getStats(empresaDocs);
        const avalStats = getStats(avalDocs);

        return (
            <div className="flex flex-column gap-3">
                <AppTableCard animated={false}>
                    <table className="tk-table tk-table--compact">
                        <thead>
                            <tr>
                                <th>Actor</th>
                                <th className="tk-th-center">Meses Evaluados</th>
                                <th className="tk-th-right">Renta PEI</th>
                                <th className="tk-th-right">Saldo Prom. Total</th>
                                <th className="tk-th-right">Ratio Cap. Pago</th>
                                <th>Origen</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-2 font-bold text-900 border-right-1 surface-border">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-building text-blue-600" /> Empresa
                                    </div>
                                </td>
                                <td className="p-2 tk-td-center text-600 font-medium">{empStats.n}</td>
                                <td className="p-2 tk-td-right">{formatCurrency(renta_pei)}</td>
                                <td className="p-2 tk-td-right font-medium">{empStats.n ? formatCurrency(empStats.avgPromedio) : <NA />}</td>
                                <td className="p-2 tk-td-right font-bold" style={{ color: PURPLE }}>{empStats.n ? formatRatio(empStats.ratio) : <NA />}</td>
                                <td className="p-2 pl-3 text-500 text-xs">{empStats.sourceType}</td>
                            </tr>
                            <tr>
                                <td className="p-2 font-bold text-900 border-right-1 surface-border">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-user text-green-600" /> Aval
                                    </div>
                                </td>
                                <td className="p-2 tk-td-center text-600 font-medium">{avalStats.n}</td>
                                <td className="p-2 tk-td-right">{formatCurrency(renta_pei)}</td>
                                <td className="p-2 tk-td-right font-medium">{avalStats.n ? formatCurrency(avalStats.avgPromedio) : <NA />}</td>
                                <td className="p-2 tk-td-right font-bold" style={{ color: PURPLE }}>{avalStats.n ? formatRatio(avalStats.ratio) : <NA />}</td>
                                <td className="p-2 pl-3 text-500 text-xs">{avalStats.sourceType}</td>
                            </tr>
                        </tbody>
                    </table>
                </AppTableCard>

                <div className="bg-white border-round-lg p-3 shadow-1 border-1 surface-border">
                    <div className="text-sm font-bold text-900 mb-2">Lectura Ejecutiva</div>
                    <ul className="m-0 pl-3 text-sm text-700" style={{ lineHeight: 1.6 }}>
                        <li>La <strong>Renta PEI</strong> requerida mensual es de {formatCurrency(renta_pei)}.</li>
                        <li>
                            {empStats.n > 0 ? (
                                <>La empresa alcanza un ratio de <strong>{formatRatio(empStats.ratio)}</strong> ({empStats.n} meses validos).</>
                            ) : <>La empresa no cuenta con historial bancario suficiente.</>}
                        </li>
                        <li>
                            {avalStats.n > 0 ? (
                                <>El aval alcanza un ratio de <strong>{formatRatio(avalStats.ratio)}</strong> ({avalStats.n} meses validos).</>
                            ) : <>El aval no ha presentado estados de cuenta o están en revisión.</>}
                        </li>
                        {(!renta_pei || renta_pei === 0) && (
                            <li className="text-orange-500">Advertencia: No se detectó renta_pei en el cotizador por lo que los ratios no se pueden calcular adecuadamente.</li>
                        )}
                    </ul>
                </div>
            </div>
        );
    };

    const pmViews = [
        { key: 'resumen', label: 'Resumen', icon: 'pi pi-th-large', color: PURPLE },
        { key: 'empresa', label: 'Empresa', icon: 'pi pi-building', color: '#1565C0' },
        { key: 'aval', label: 'Aval / Garante', icon: 'pi pi-user', color: '#2E7D32' },
    ];

    const renderCapPagoContent = () => {
        if (persona_mode === 'pf') {
            return <CapPagoTable actor="pf" documents={documents} rentaPei={renta_pei} onViewDoc={openEditForm} />;
        }

        if (selectedView === 'empresa') {
            return <CapPagoTable actor="empresa" documents={documents} rentaPei={renta_pei} onViewDoc={openEditForm} />;
        }

        if (selectedView === 'aval') {
            return <CapPagoTable actor="aval" documents={documents} rentaPei={renta_pei} onViewDoc={openEditForm} />;
        }

        return renderResumenPM();
    };

    return (
        <div className="flex flex-column gap-3">
            {/* Header */}
            <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-wallet text-lg" style={{ color: PURPLE }} />
                    <span className="text-sm font-bold" style={{ color: PURPLE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Capacidad de Pago {persona_mode === 'pm' ? '(Persona Moral)' : '(Persona Física)'}
                    </span>
                </div>
                <AppIconButton icon="pi pi-refresh" round onClick={() => fetchData()} loading={loading} title="Actualizar" />
            </div>

            {persona_mode === 'pm' && (
                <div className="flex align-items-center gap-2 flex-wrap">
                    {pmViews.map((view) => {
                        const isActive = selectedView === view.key;
                        return (
                            <button
                                key={view.key}
                                onClick={() => setSelectedView(view.key)}
                                style={getChipButtonStyle(isActive, view.color)}
                            >
                                <i className={view.icon} style={{ fontSize: '0.7rem' }} />
                                {view.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Dictamen calculado en backend (#2): fuente unica de los umbrales */}
            <CapacidadPagoDictamen persona={persona} toast={toast} />

            <div className="flex align-items-center gap-2 pt-2 border-top-1 surface-border">
                <i className="pi pi-table text-sm text-500" />
                <span className="text-sm font-bold text-700" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Detalle operativo por estado de cuenta
                </span>
            </div>

            {renderCapPagoContent()}

            {/* Documentos fuente */}
            {/*  {documents.length > 0 && (
                <div>
                    <div className="flex align-items-center gap-2 mb-2 pb-2 border-bottom-1 surface-border">
                        <i className="pi pi-file text-base" style={{ color: PURPLE }} />
                        <span className="text-sm font-bold text-700" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Documentos fuente
                        </span>
                        <span className="ml-auto text-xs text-400">
                            Edita los datos operativos por estado de cuenta
                        </span>
                    </div>
                    <div className="flex flex-column gap-2">
                        {documents.map(doc => {
                            const isRejected = doc.review_status === 'rejected';
                            const banco = doc.resolved_extracted?.banco || doc.resolved_extracted?.nombre || '—';
                            const actorLabel = doc.actor === 'empresa' ? 'Empresa' : doc.actor === 'aval' ? 'Aval' : 'PF';
                            const actorColor = doc.actor === 'empresa' ? '#1565C0' : doc.actor === 'aval' ? '#2E7D32' : PURPLE;
                            return (
                                <div key={doc.id} className="bg-white border-1 surface-border border-round-lg overflow-hidden shadow-1"
                                    style={{ borderLeft: `4px solid ${actorColor}` }}>
                                    <div className="flex align-items-center gap-3 p-3">
                                        <i className="pi pi-bank text-sm" style={{ color: actorColor }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="text-sm font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {banco}
                                            </div>
                                            <div className="text-xs text-500 mt-1 flex align-items-center gap-2">
                                                <span style={{ color: actorColor, fontWeight: 600 }}>{actorLabel}</span>
                                                <span>|</span>
                                                <span>{doc.periodo || doc.file_name}</span>
                                                {doc.has_manual_overrides && (
                                                    <span className="text-xs font-semibold px-1 border-round"
                                                        style={{ background: PURPLE_LIGHT, color: PURPLE }}>
                                                        ✓ Corregido ({doc.manual_override_count})
                                                    </span>
                                                )}
                                                {doc.operational_completeness?.missing_required_paths?.length > 0 && (
                                                    <span className="text-xs font-semibold px-1 border-round"
                                                        style={{ background: '#FFF8E1', color: '#E65100' }}>
                                                        Faltan {doc.operational_completeness.missing_required_paths.length}
                                                    </span>
                                                )}
                                                {isRejected && (
                                                    <span className="text-xs font-semibold px-1 border-round"
                                                        style={{ background: '#fee2e2', color: '#dc2626' }}>
                                                        Rechazado
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <AppButton label="Editar" icon="pi pi-pencil" className='Button p-0 ' variant="info" size="sm" disabled={isRejected}
                                            onClick={() => openEditForm(doc)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )} */}

            {/* ── Modal de edición ── */}
            <OperationalReviewModal
                isOpen={!!modalDoc}
                onClose={() => setModalDoc(null)}
                title="Editar Estado de Cuenta"
                subtitle={modalDoc ? `${modalDoc.resolved_extracted?.banco || modalDoc.file_name || 'Documento'} — ${modalDoc.periodo || 'Sin periodo'}` : ''}
                accentColor={PURPLE}
                documentUrl={modalDoc?.signed_url}
                documentName={modalDoc?.file_name}
                saving={savingOverride}
                loading={loadingOverrides}
                metaContent={modalDoc ? (
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <Tag value={modalDoc.actor === 'empresa' ? 'Empresa' : modalDoc.actor === 'aval' ? 'Aval' : 'PF'}
                            style={{
                                fontSize: '0.7rem', padding: '0.15rem 0.5rem',
                                background: modalDoc.actor === 'empresa' ? '#E3F2FD' : modalDoc.actor === 'aval' ? '#E8F5E9' : PURPLE_LIGHT,
                                color: modalDoc.actor === 'empresa' ? '#1565C0' : modalDoc.actor === 'aval' ? '#2E7D32' : PURPLE
                            }} rounded />
                        {modalDoc.resolved_extracted?.banco && (
                            <span className="text-xs text-500">Banco: <strong>{modalDoc.resolved_extracted.banco}</strong></span>
                        )}
                        {modalDoc.periodo && (
                            <span className="text-xs text-500">Periodo: <strong>{modalDoc.periodo}</strong></span>
                        )}
                        {modalDoc.has_manual_overrides && (
                            <Tag value={`Corregido (${modalDoc.manual_override_count})`} rounded
                                style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: PURPLE_LIGHT, color: PURPLE }} />
                        )}
                        {modalDoc.operational_completeness?.missing_required_paths?.length > 0 && (
                            <Tag value={`Faltan ${modalDoc.operational_completeness.missing_required_paths.length}`} severity="warning" rounded style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} />
                        )}
                    </div>
                ) : null}
            >
                {modalDoc && (
                    <CapPagoEditForm
                        doc={modalDoc}
                        activeOverridePaths={activeOverridePaths}
                        editValues={editValues}
                        saveReason={saveReason}
                        onFieldChange={(path, val) => setEditValues(v => ({ ...v, [path]: val }))}
                        onReasonChange={setSaveReason}
                        onClearPath={handleClearPath}
                        onSave={() => saveOverrides(modalDoc)}
                        onCancel={() => setModalDoc(null)}
                        saving={savingOverride}
                    />
                )}
            </OperationalReviewModal>
        </div>
    );
};

export default CapPagoAnalysisPanel;
